import { useMutation, useQuery } from '@apollo/client/react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AcceptPendingInvitationDocument, GetMyPendingInvitationsDocument } from '@/graphql';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useAuth } from '../context';
import { translateAuthError } from '../utils';
import { useSwitchAccount } from './use-switch-account';

/**
 * Invitations waiting for the signed-in user, plus a one-click join.
 *
 * Signing in never joins an organization on its own: anyone who knows an address could
 * otherwise pull that person in without an act of consent. So the invitation is surfaced
 * here and accepted explicitly — the pattern GitHub, Slack and Google Workspace use.
 */
export function usePendingInvitations() {
  const { t } = useTranslation();
  const tMasterRole = useEnumTranslation('masterAccountRole');
  const { isAuthenticated, refetch: refetchAuth } = useAuth();
  const { switchToMasterAccount } = useSwitchAccount();
  const { showError } = useToastHelpers();
  const { data, refetch } = useQuery(GetMyPendingInvitationsDocument, { skip: !isAuthenticated });
  const [acceptMutation, { loading }] = useMutation(AcceptPendingInvitationDocument);
  const invitations = data?.myPendingInvitations.invitations;

  const accept = useCallback(
    async (invitationId: string) => {
      // errorPolicy: 'all' resolves GraphQL errors instead of throwing — an expired or
      // reassigned invitation must not look like it worked.
      const result = await acceptMutation({ variables: { invitationId } });
      if (result.error) {
        // The list is refetched so an invitation that is gone stops being offered, and the
        // reason is shown — the row simply vanishing would read as success.
        showError(translateAuthError(result.error, t));
        await refetch();
        return;
      }

      // A master account invitation is accepted to work in that master account: go straight to its board. The
      // switch navigates on success; on failure it has shown why, and the session is re-read so
      // the switcher offers the new master account.
      const masterAccount = invitations?.find(
        invitation => invitation.id === invitationId
      )?.masterAccount;
      if (masterAccount) {
        if (!(await switchToMasterAccount(masterAccount.id))) {
          await Promise.all([refetchAuth(), refetch()]);
        }
        return;
      }

      // The new membership changes what the switcher offers and what the session may reach,
      // so reload rather than patch the cache — same reasoning as switching accounts.
      window.location.reload();
    },
    [acceptMutation, invitations, refetch, refetchAuth, showError, switchToMasterAccount, t]
  );

  return {
    // An invitation leads into an organization or into a master account; a master account's names the role offered.
    invitations: (invitations ?? []).map(invitation => ({
      id: invitation.id,
      accountName: invitation.account?.name ?? invitation.masterAccount?.name ?? '',
      ...(invitation.masterAccount && invitation.masterRoles.length > 0
        ? { detail: invitation.masterRoles.map(role => tMasterRole(role)).join(', ') }
        : {})
    })),
    accept,
    accepting: loading
  };
}
